import { useTheme } from '@emotion/react';
import { Box, useMediaQuery } from '@mui/material';
import React from 'react';
import Shimmer from './Shimmer';

// Placeholder for <AsyncTableListing />. Geometry comes from the real table
// styles in index.scss - `thead th` and `tbody td` are both `padding: 18px 28px`
// with a 1px row border, and the header sits on #F4F2FE - so the shimmering
// table is the same height as the one that replaces it.
const CELL_PADDING = '18px 28px';

const TableListingSkeleton = ({
  // [{ width, cellWidth, align }] - one entry per column. `width` sizes the
  // header label, `cellWidth` the body cell.
  columns = [],
  rows = 5,
  rowHeight = 24,
  // Renders the Previous / per-page / pages / Next footer the real listing has.
  showPagination = false,
  // A leading mark in the first cell: a round avatar for people listings
  // (investors, profile viewers) or a square file icon for document listings.
  // 'avatar' also renders a second, smaller line beneath the name (the email).
  leadingAvatar = false,
  leading = null, // 'avatar' | 'icon'
  // Some avatar listings show only a name (investor network); others show a name
  // over an email (pipeline, profile views).
  leadingSubline = true,
  // AsyncTableListing renders cards instead of a table below `sm`, so the
  // skeleton has to switch at the same breakpoint - a shimmering table on a
  // phone would be the wrong shape and the page would jump on load.
  mobileCards = true,
  // Matches AsyncTableListing's cardFieldLimit: the label/value pairs per card.
  cardFieldLimit = 4,
  sx = {},
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const leadingKind = leading || (leadingAvatar ? 'avatar' : null);

  // Columns need a floor, or with `width: max-content` they collapse to the
  // shimmer inside them and the columns stop lining up with the header.
  const flexFor = (column) => ({
    minWidth: column.minWidth ?? 120,
    flex: column.grow === false ? '0 0 auto' : '1 1 auto',
    display: 'flex',
    justifyContent: column.align === 'end' ? 'flex-end' : 'flex-start',
  });

  if (isSmallScreen && mobileCards) {
    // Same shape as MobileCardList: heading (with its leading mark), a two-up
    // grid of label/value pairs, then the actions footer.
    const fieldCount = Math.min(
      Math.max(columns.length - 2, 1),
      cardFieldLimit
    );
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, p: 1.25,  ...sx, }}>
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <Box
            key={rowIndex}
            sx={{
              border: `1px solid ${theme.palette.customColors.gray200 || '#EAECF0'}`,
              borderRadius: '12px',
              backgroundColor: '#fff',
              padding: '0.875rem',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              minWidth: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {leadingKind && (
                <Shimmer
                  width={40}
                  height={40}
                  radius={leadingKind === 'avatar' ? 20 : 8}
                />
              )}
              <Box sx={{ flex: 1, minWidth: 0, display: 'grid', gap: '6px' }}>
                <Shimmer width="60%" height={15} sx={{ maxWidth: 170 }} />
                {leadingKind === 'avatar' && leadingSubline && (
                  <Shimmer width="80%" height={12} sx={{ maxWidth: 200 }} />
                )}
              </Box>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: '0.625rem 0.75rem',
                borderTop: `1px solid ${theme.palette.customColors.gray200 || '#EAECF0'}`,
                pt: 1,
              }}
            >
              {Array.from({ length: fieldCount }).map((_, index) => (
                <Box key={index} sx={{ minWidth: 0 }}>
                  <Shimmer width="55%" height={10} sx={{ maxWidth: 70, mb: '4px' }} />
                  <Shimmer width="75%" height={14} sx={{ maxWidth: 110 }} />
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 0.5,
                borderTop: `1px solid ${theme.palette.customColors.gray200 || '#EAECF0'}`,
                pt: 1,
              }}
            >
              <Shimmer width={92} height={34} radius={8} />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        borderRadius: '16px',
        border: `1px solid ${theme.palette.customColors.gray300}`,
        bgcolor: 'white',
        // The real table scrolls sideways when its columns do not fit, so the
        // skeleton has to as well - otherwise it would be the only thing on the
        // page forcing the viewport wider than the screen.
        overflowX: 'auto',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        ...sx,
      }}
    >
      {/* Sized to its columns, but never narrower than the card. */}
      <Box sx={{ minWidth: '100%', width: 'max-content' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#F4F2FE',
            padding: CELL_PADDING,
          }}
        >
          {columns.map((column, index) => (
            <Box key={index} sx={flexFor(column)}>
              <Shimmer width={column.width ?? 72} height={16} />
            </Box>
          ))}
        </Box>

        {Array.from({ length: rows }).map((_, rowIndex) => (
          <Box
            key={rowIndex}
            sx={{
              display: 'flex',
              alignItems: 'center',
              padding: CELL_PADDING,
              borderBottom: rowIndex === rows - 1 ? 'none' : '1px solid #ddd',
            }}
          >
            {columns.map((column, index) => (
              <Box
                key={index}
                sx={{ ...flexFor(column), alignItems: 'center', gap: '12px' }}
              >
                {/* The first cell leads with a 40px mark - a round avatar for a
                    person, a square icon for a file - then the name (plus an
                    email line, for people). */}
                {leadingKind && index === 0 ? (
                  <>
                    <Shimmer
                      width={40}
                      height={40}
                      radius={leadingKind === 'avatar' ? 20 : 8}
                    />
                    <Box sx={{ display: 'grid', gap: '6px', minWidth: 0 }}>
                      <Shimmer width={column.cellWidth ?? 110} height={15} />
                      {leadingKind === 'avatar' && leadingSubline && (
                        <Shimmer width={column.subWidth ?? 150} height={12} />
                      )}
                    </Box>
                  </>
                ) : (
                  <Shimmer
                    width={column.cellWidth ?? column.width ?? 72}
                    height={column.cellHeight ?? rowHeight}
                    radius={column.cellRadius ?? 6}
                  />
                )}
              </Box>
            ))}
          </Box>
        ))}
      </Box>

      {showPagination && (
        // Sticks to the card, mirrors the real footer: Previous + per-page on the
        // left, page numbers centred, Next on the right. Wraps on a phone rather
        // than forcing the card wider.
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '0.75rem',
            padding: { xs: '1rem', md: '1rem 1.5rem' },
            borderTop: '1px solid #ddd',
            position: 'sticky',
            left: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Shimmer width={110} height={44} radius={10} />
            <Shimmer width={136} height={44} radius={10} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {[0, 1, 2].map((page) => (
              <Shimmer key={page} width={28} height={28} radius={6} />
            ))}
          </Box>
          <Shimmer width={96} height={44} radius={10} />
        </Box>
      )}
    </Box>
  );
};

export default TableListingSkeleton;
